import React, { useMemo } from 'react';
import { Typography, Tag } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import CrudPage from '../../../components/common/CrudPage';
import eventTypeService from '../../../services/cms/eventTypeServiceRequest';
import eventCategoryService from '../../../services/cms/eventCategoryServiceRequest';
import { safeParseColumns } from '../../../utils/utility';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { label: 'Active', value: 1 },
  { label: 'Inactive', value: 0 },
];

const QuanLyTheLoaiSuKien = () => {
  // Danh mục sự kiện: dùng cho select "Danh mục" trong form và map tên trên cột DANH MỤC.
  // Dùng getList thay listActive: list-active bị CORS trên staging -> map rỗng -> cột DANH MỤC trơ ra id.
  const { data: categoriesData } = useQuery({
    queryKey: ['allEventCategories'],
    queryFn: () => eventCategoryService.getList({ page: 1, limit: 1000 }),
  });
  const categoriesList = useMemo(
    () => categoriesData?.data?.data || categoriesData?.data || [],
    [categoriesData]
  );

  const categoryMap = useMemo(() => {
    const map = {};
    categoriesList.forEach((item) => {
      map[item.id] = item.title || item.name;
    });
    return map;
  }, [categoriesList]);

  const categoryOptions = useMemo(() => {
    return categoriesList.map((item) => ({
      label: `[${item.id}] ${item.title || item.name}`,
      value: item.id,
    }));
  }, [categoriesList]);

  // Parity CMS cũ: list thể loại lọc được theo danh mục sự kiện (categorySearch)
  const filterFields = useMemo(
    () => [
      {
        label: 'Danh mục',
        value: 'event_category_id',
        options: [{ label: 'Tất cả danh mục', value: '' }, ...categoryOptions],
      },
      {
        label: 'Trạng thái',
        value: 'status',
        options: [
          { label: 'Tất cả trạng thái', value: '' },
          { label: 'Active', value: '1' },
          { label: 'Inactive', value: '0' },
        ],
      },
    ],
    [categoryOptions]
  );

  const formFields = useMemo(
    () => [
      {
        name: 'name',
        label: 'Tên thể loại',
        type: 'input',
        rules: [{ required: true, message: 'Vui lòng nhập tên thể loại!' }],
        placeholder: 'Ví dụ: Sinh nhật, Lễ hội...',
      },
      {
        name: 'event_category_id',
        label: 'Danh mục',
        type: 'select',
        options: categoryOptions,
        placeholder: 'Chọn danh mục',
        propsInput: { showSearch: true, allowClear: true, optionFilterProp: 'label' },
      },
      {
        name: 'description',
        label: 'Mô tả',
        type: 'textarea',
        placeholder: 'Mô tả thể loại sự kiện',
      },
      { type: 'divider', label: 'Cột giá trị' },
      {
        name: 'columns_rows',
        type: 'group',
        isSort: true,
        titleButtonAdd: 'Thêm cột',
        filedsList: [
          { name: 'title', type: 'input', placeholder: 'Tên cột', rules: [{ required: true, message: 'Vui lòng nhập tên cột!' }] },
        ],
      },
      // Giống CMS cũ: mỗi ảnh vừa upload được vừa dán được link có sẵn (điền cả hai thì link thắng)
      { type: 'divider', label: 'Hình ảnh' },
      {
        name: 'image_url',
        label: 'Ảnh',
        type: 'upload-cdn',
        placeholder: 'URL ảnh',
        span: 12,
        enterLink: true,
        linkPlaceholder: 'Link ảnh',
      },
      {
        name: 'icon',
        label: 'Icon',
        type: 'upload-cdn',
        placeholder: 'URL icon',
        span: 12,
        enterLink: true,
        linkPlaceholder: 'Link icon',
      },
      {
        name: 'background',
        label: 'Ảnh nền',
        type: 'upload-cdn',
        placeholder: 'URL ảnh nền',
        span: 12,
        enterLink: true,
        linkPlaceholder: 'Link ảnh nền',
      },
      {
        name: 'background_countdown',
        label: 'Ảnh nền đếm ngược',
        type: 'upload-cdn',
        placeholder: 'URL ảnh nền đếm ngược',
        span: 12,
        enterLink: true,
        linkPlaceholder: 'Link ảnh nền đếm ngược',
      },
      { type: 'divider', label: 'Khác' },
      {
        name: 'alert_info',
        label: 'Thông báo',
        type: 'textarea',
        placeholder: 'Thông tin thông báo (JSON)',
        propsInput: { rows: 2 },
      },
      {
        name: 'order_place',
        label: 'Thứ tự hiển thị',
        type: 'number',
        span: 12,
        propsInput: { min: 0, className: 'w-full' },
      },
      {
        name: 'status',
        label: 'Trạng thái',
        type: 'select',
        options: STATUS_OPTIONS,
        defaultValue: 1,
        span: 12,
      },
    ],
    [categoryOptions]
  );

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 60,
      },
      {
        title: 'TÊN THỂ LOẠI',
        dataIndex: 'name',
        key: 'name',
        render: (text) => <Text strong>{text}</Text>,
      },
      {
        title: 'DANH MỤC',
        dataIndex: 'event_category_id',
        key: 'event_category_id',
        width: 150,
        render: (val) => (val ? categoryMap[val] || `[${val}]` : '-'),
      },
      {
        title: 'MÔ TẢ',
        dataIndex: 'description',
        key: 'description',
        width: 200,
        ellipsis: true,
      },
      {
        title: 'ẢNH',
        dataIndex: 'image_url',
        key: 'image_url',
        width: 80,
        render: (url) =>
          url ? <img src={url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : '-',
      },
      {
        title: 'ẢNH NỀN',
        dataIndex: 'background',
        key: 'background',
        width: 90,
        render: (url) =>
          url ? <img src={url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : '-',
      },
      {
        title: 'ICON',
        dataIndex: 'icon',
        key: 'icon',
        width: 60,
        render: (url) =>
          url ? <img src={url} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} /> : '-',
      },
      {
        title: 'THỨ TỰ',
        dataIndex: 'order_place',
        key: 'order_place',
        width: 80,
        align: 'center',
      },
      {
        title: 'TRẠNG THÁI',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status) => (
          <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? 'ACTIVE' : 'INACTIVE'}</Tag>
        ),
      },
      {
        title: 'NGÀY TẠO',
        dataIndex: 'create_time',
        key: 'create_time',
        width: 140,
        render: (time) => (time ? dayjs(time).format('DD/MM/YYYY HH:mm') : '-'),
      },
    ],
    [categoryMap]
  );

  // DB lưu columns là chuỗi JSON mảng tên cột — form dùng dạng dòng {title} để thêm/xóa/kéo thả
  const mapDataToForm = (record) => ({
    columns_rows: safeParseColumns(record.columns).map((title) => ({ title })),
    event_category_id: record.event_category_id || undefined,
  });

  const mapFormToData = (values) => ({
    columns: JSON.stringify((values.columns_rows || []).map((row) => row?.title?.trim()).filter(Boolean)),
    columns_rows: undefined, // key ảo, validator API không nhận — undefined sẽ bị loại khi serialize
    event_category_id: values.event_category_id ?? null, // gửi null khi bỏ chọn để clear được trên DB
  });

  return (
    <CrudPage
      title="QUẢN LÝ THỂ LOẠI SỰ KIỆN"
      subTitle="Quản lý danh sách thể loại sự kiện trong hệ thống"
      itemsName="Thể loại sự kiện"
      queryKey="eventTypeList"
      service={eventTypeService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm kiếm theo tên thể loại..."
      addButtonLabel="Thêm thể loại"
      formConfig={{ width: 900 }}
      mapDataToForm={mapDataToForm}
      mapFormToData={mapFormToData}
    />
  );
};

export default QuanLyTheLoaiSuKien;
